import { Empty, Typography, theme as antdTheme } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';

interface Props {
  /** Big lead-in (e.g. "No invoices yet"). */
  title: ReactNode;
  /** Supporting line (e.g. "Generate a draft to start collecting."). */
  description?: ReactNode;
  /** Icon shown above the title; defaults to a generic inbox. */
  icon?: ReactNode;
  /** Primary CTA — usually a `<Button>`. Right-place for "New invoice". */
  action?: ReactNode;
  /** Secondary line (links, faint text). */
  hint?: ReactNode;
  /** Use the original AntD Empty illustration instead of an icon. */
  illustrated?: boolean;
  /** Centering padding — default 32px each side. */
  padded?: boolean;
}

/**
 * Upgrades AntD `<Empty>` with a structured title/description/action layout
 * so empty list states stop being four-line ad-hoc divs. Use `illustrated` for
 * the classic Empty box image, otherwise it shows a soft icon — quieter and
 * more table-friendly.
 */
export function EmptyState({
  title,
  description,
  icon,
  action,
  hint,
  illustrated = false,
  padded = true,
}: Props) {
  const { token } = antdTheme.useToken();
  if (illustrated) {
    return (
      <div style={{ padding: padded ? '32px 16px' : 0, textAlign: 'center' }}>
        <Empty description={
          <div>
            <Typography.Text strong style={{ display: 'block', fontSize: 14 }}>
              {title}
            </Typography.Text>
            {description && (
              <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 13 }}>
                {description}
              </Typography.Text>
            )}
          </div>
        }>
          {action}
          {hint && <div style={{ marginTop: 8, fontSize: 12, color: token.colorTextTertiary }}>{hint}</div>}
        </Empty>
      </div>
    );
  }
  return (
    <div
      style={{
        padding: padded ? '40px 16px' : 0,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 12,
          background: token.colorFillTertiary,
          color: token.colorTextSecondary,
          display: 'grid',
          placeItems: 'center',
          fontSize: 28,
        }}
      >
        {icon ?? <InboxOutlined />}
      </div>
      <Typography.Text strong style={{ fontSize: 15 }}>{title}</Typography.Text>
      {description && (
        <Typography.Text type="secondary" style={{ fontSize: 13, maxWidth: 360 }}>
          {description}
        </Typography.Text>
      )}
      {action && <div style={{ marginTop: 4 }}>{action}</div>}
      {hint && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{hint}</Typography.Text>}
    </div>
  );
}
